import { find, first, isEmpty, isString, toLower } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSciDomain } from 'components';
import { BANK_STYLES } from 'constants/bank-styles';
import { IMAGES_PATH } from 'constants/images.constants';
import {
  PAYMENT_TYPES_WITH_PAYMENT_LINK_QR,
  SQ_PAYMENT_TYPES_SKIP_QR,
} from 'constants/payment-type.constants';
import {
  Bank as BankEnum,
  CancelStatusDetails,
  EventName,
  FormFieldType,
  NewStatusDetails,
  OrderStatus,
  PaymentStatusView,
  PaymentType,
  SciTemplate,
} from 'enums';
import { TranslationNamespace } from 'i18n';
import {
  PaymentFormValues,
  OrderViewProps,
  PaymentField,
  RequisitesCard,
  RequisitesSBP,
  Requisites,
} from 'types';
import { eventUtils, formatUtils } from 'utils';

import { isPhoneNumberKgs } from './isPhoneNumberKgs';

import {
  useBankUtils,
  useDeeplinks,
  useOrderActions,
  useOrderAuth,
  useOrderData,
  useOrderReceipts,
  usePaymentForm,
  usePaymentTypeFlags,
  useSelectingAlternatives,
  useStaticData,
  useTradeMethodSelection,
} from './';

const INITIAL_FORM_VALUES: PaymentFormValues = {
  customerCardFirstDigits: null,
  customerCardLastDigits: null,
  customerName: null,
  customerBank: null,
  customerPhoneLastDigits: null,
  customerUtr: null,
  customerIBAN: null,
  customerAccountNumber: null,
};

const PAYMENT_TYPE_REQUISITES_FIELD_FALLBACKS: Partial<
  Record<PaymentType, Array<{ name: string; type: string }>>
> = {
  [PaymentType.Card2Card]: [
    { name: 'cardInfo', type: 'card' },
    { name: 'cardholder', type: 'cardholder' },
    { name: 'expirationDate', type: 'expiration_date' },
  ],
  [PaymentType.TCard2Card]: [
    { name: 'cardInfo', type: 'card' },
    { name: 'cardholder', type: 'cardholder' },
    { name: 'expirationDate', type: 'expiration_date' },
  ],
  [PaymentType.SBP]: [
    { name: 'phone', type: 'phone' },
    { name: 'cardholder', type: 'holder' },
    { name: 'accountLastDigits', type: 'account_last_digits' },
    { name: 'paymentLink', type: 'payment_link' },
  ],
  [PaymentType.TSBP]: [
    { name: 'phone', type: 'phone' },
    { name: 'cardholder', type: 'holder' },
    { name: 'accountLastDigits', type: 'account_last_digits' },
  ],
  [PaymentType.SberPay]: [
    { name: 'phone', type: 'phone' },
    { name: 'cardholder', type: 'holder' },
    { name: 'accountLastDigits', type: 'account_last_digits' },
  ],
  [PaymentType.Deeplink]: [
    { name: 'cardInfo', type: 'card' },
    { name: 'phone', type: 'phone' },
    { name: 'cardholder', type: 'holder' },
    { name: 'accountLastDigits', type: 'account_last_digits' },
    { name: 'passportSeries', type: 'passport_series' },
    { name: 'passportNumber', type: 'passport_number' },
    { name: 'passportIssueDate', type: 'passport_issue_date' },
    { name: 'firstName', type: 'first_name' },
    { name: 'lastName', type: 'last_name' },
    { name: 'middleName', type: 'middle_name' },
    { name: 'birthDate', type: 'birth_date' },
  ],
  [PaymentType.PhoneNumber]: [
    { name: 'phone', type: 'phone' },
    { name: 'cardholder', type: 'holder' },
    { name: 'accountLastDigits', type: 'account_last_digits' },
    { name: 'paymentLink', type: 'payment_link' },
  ],
  [PaymentType.SIM]: [
    { name: 'phone', type: 'phone' },
    { name: 'cardholder', type: 'holder' },
    { name: 'paymentLink', type: 'payment_link' },
  ],
  [PaymentType.TSim]: [
    { name: 'phone', type: 'phone' },
    { name: 'cardholder', type: 'holder' },
  ],
  [PaymentType.AccountNumber]: [
    { name: 'accountNumber', type: 'account_number' },
    { name: 'cardholder', type: 'holder' },
    { name: 'bic', type: 'bic' },
    { name: 'accountLastDigits', type: 'account_last_digits' },
    { name: 'taxId', type: 'tax_id' },
  ],
  [PaymentType.TransferViaIdCard]: [
    { name: 'idCard', type: 'id_card' },
    { name: 'cardholder', type: 'holder' },
  ],
  [PaymentType.AccountNumberIBAN]: [
    { name: 'accountNumber', type: 'iban' },
    { name: 'cardholder', type: 'holder' },
  ],
  [PaymentType.AccountNumberSEPA]: [
    { name: 'accountNumber', type: 'iban' },
    { name: 'cardholder', type: 'holder' },
    { name: 'swiftBic', type: 'swift_bic' },
  ],
  [PaymentType.UPI]: [
    { name: 'accountNumber', type: 'upi_id' },
    { name: 'cardholder', type: 'holder' },
    { name: 'paymentLink', type: 'payment_link' },
  ],
  [PaymentType.IMPS]: [
    { name: 'accountNumber', type: 'imps_account_number' },
    { name: 'swiftBic', type: 'ifsc' },
    { name: 'cardholder', type: 'beneficiary_name' },
  ],
  [PaymentType.PhonePE]: [
    { name: 'phone', type: 'phone' },
    { name: 'accountNumber', type: 'upi_id' },
  ],
  [PaymentType.ERIP]: [{ name: 'accountNumber', type: 'erip' }],
  [PaymentType.PaymentLink]: [
    { name: 'paymentLink', type: 'payment_link' },
    { name: 'cardholder', type: 'holder' },
  ],
  [PaymentType.NSPK]: [
    { name: 'qrManagerApiKey', type: 'qr_manager_api_key' },
    { name: 'qrManagerLogin', type: 'qr_manager_login' },
    { name: 'paymentLink', type: 'payment_link' },
    { name: 'paymentString', type: 'payment_string' },
    { name: 'passportSeries', type: 'passport_series' },
    { name: 'passportNumber', type: 'passport_number' },
    { name: 'passportIssueDate', type: 'passport_issue_date' },
    { name: 'firstName', type: 'first_name' },
    { name: 'lastName', type: 'last_name' },
    { name: 'middleName', type: 'middle_name' },
    { name: 'birthDate', type: 'birth_date' },
  ],
};

export type OrderViewModelResult =
  | { isLoading: true; authenticationFailed: false }
  | { isLoading: false; authenticationFailed: true }
  | ({ isLoading: false; authenticationFailed: false } & OrderViewProps);

export const useOrderViewModel = (): OrderViewModelResult => {
  const [paymentFormValues, setPaymentFormValues] =
    useState<PaymentFormValues>(INITIAL_FORM_VALUES);

  const {
    orderId,
    authenticated,
    authenticationFailed,
    setAuthenticationFailed,
    isAuthLoading,
  } = useOrderAuth();

  const {
    order,
    initialOrderFetched,
    isOrderLoading,
    apiActionOptions,
    refetchOrder,
    refetchOrderStatus,
  } = useOrderData({
    orderId,
    authenticated,
    setAuthenticationFailed,
    setPaymentFormValues,
  });

  const staticData = useStaticData({ enabled: !!order });

  const tradeMethodSelection = useTradeMethodSelection({
    order,
    authenticated,
  });

  const paymentTypeFlags = usePaymentTypeFlags({
    selectedTradeMethod: tradeMethodSelection.selectedTradeMethod,
    order,
  });

  const paymentForm = usePaymentForm({
    orderId,
    selectedTradeMethod: tradeMethodSelection.selectedTradeMethod,
    order,
    isAmobile: paymentTypeFlags.isAmobile,
    paymentFormValues,
  });

  const bankUtils = useBankUtils({
    banks: staticData.banks,
    order,
  });

  const receipts = useOrderReceipts({
    orderId,
    order,
    onApiError: apiActionOptions.onError,
  });

  const selectingAlternativeSum = useSelectingAlternatives({
    orderId,
    order,
    onSuccess: apiActionOptions.onSuccess,
    onError: apiActionOptions.onError,
  });

  const { data: sciDomainConfig } = useSciDomain();

  const { t: tOrderButtons } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'pages.order.buttons',
  });

  const currencySymbol = useMemo(
    () => find(staticData.currencies, ['code', order?.currency])?.symbol ?? '',
    [staticData.currencies, order?.currency],
  );

  const actionLabels = useMemo(() => {
    const defaultLabels = {
      cancel: tOrderButtons('cancel'),
      confirmCard: tOrderButtons('confirm_card'),
      confirmPayment: tOrderButtons('confirm_transfer'),
      backToShop: tOrderButtons('back_to_shop'),
      selectTradeMethod: tOrderButtons('select_trade_method'),
    };

    if (sciDomainConfig?.template === SciTemplate.SQ) {
      const sqLabels = {
        ...defaultLabels,
        confirmPayment: tOrderButtons('confirm_transfer_sq', {
          amount: order?.amount,
          currencySymbol,
        }),
      };
      return sqLabels;
    }

    return defaultLabels;
  }, [sciDomainConfig?.template, tOrderButtons, order?.amount, currencySymbol]);

  const orderActions = useOrderActions({
    orderId,
    order,
    initialOrderFetched,
    paymentFormValues,
    isCustomerDataConfirmed: paymentForm.isCustomerDataConfirmed,
    customerDataBeforePayment: paymentForm.customerDataBeforePayment,
    isCardInfoConfirmDisabled: paymentForm.isCardInfoConfirmDisabled,
    setUploadReceiptModalOpen: receipts.setUploadReceiptModalOpen,
    setStartPaymentSyncFailed: tradeMethodSelection.setStartPaymentSyncFailed,
    apiActionOptions,
    actionLabels,
  });

  const isTSBPSingleBankSelection = useMemo(
    () =>
      (paymentTypeFlags.isTSBP || paymentTypeFlags.isDeeplink) &&
      paymentForm.customerFields.length === 1 &&
      paymentForm.customerFields[0]?.type === FormFieldType.Bank,
    [
      paymentTypeFlags.isTSBP,
      paymentTypeFlags.isDeeplink,
      paymentForm.customerFields,
    ],
  );

  const { confirmCard } = orderActions;

  const handlePaymentFormValuesChange = useCallback(
    (value: Partial<PaymentFormValues>) => {
      setPaymentFormValues((prev) => ({ ...prev, ...value }));

      if (
        'customerPhoneLastDigits' in value &&
        value.customerPhoneLastDigits &&
        !phoneEventFiredRef.current
      ) {
        phoneEventFiredRef.current = true;
        eventUtils.trackEvent({ event: EventName.CustomerPhoneEntered });
      }
      if ('customerBank' in value && value.customerBank) {
        eventUtils.trackEvent({ event: EventName.CustomerBankSelected });
      }
      if (
        'customerName' in value &&
        value.customerName &&
        !nameEventFiredRef.current
      ) {
        nameEventFiredRef.current = true;
        eventUtils.trackEvent({ event: EventName.CustomerNameEntered });
      }

      if (isTSBPSingleBankSelection) {
        confirmCard(value as PaymentFormValues, { silent: true });
      }
    },
    [isTSBPSingleBankSelection, confirmCard],
  );

  const isStartPaymentSyncInProgress = useMemo(
    () =>
      orderActions.isStartPaymentSyncAPICallInProgress ||
      order?.statusDetails === NewStatusDetails.NewStartPaymentSyncAttempt,
    [orderActions.isStartPaymentSyncAPICallInProgress, order?.statusDetails],
  );

  const { t: tPaymentDetails } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });

  const requisitesCountry = useMemo(() => {
    const countryCode = order?.requisites?.countryCode || '';
    return tPaymentDetails(`country.values.${toLower(countryCode)}`, {
      defaultValue:
        order?.requisites?.countryNameEn ||
        order?.requisites?.countryNameRu ||
        countryCode,
    });
  }, [
    order?.requisites?.countryCode,
    order?.requisites?.countryNameEn,
    order?.requisites?.countryNameRu,
    tPaymentDetails,
  ]);

  const requisitesBank = useMemo(
    () => order?.requisites?.bank ?? '',
    [order?.requisites?.bank],
  );

  const bankStyle = useMemo(
    () => BANK_STYLES[requisitesBank as BankEnum],
    [requisitesBank],
  );

  const getBankIconUrl = useCallback((bank: string) => {
    const style = BANK_STYLES[toLower(bank) as BankEnum];
    return style ? `${IMAGES_PATH}${style.iconPath}` : undefined;
  }, []);

  const requisitesCountryCode = useMemo(
    () => order?.requisites?.countryCode || '',
    [order?.requisites?.countryCode],
  );

  const requisitesCardInfo = useMemo(
    () => (order?.requisites as RequisitesCard)?.cardInfo || '',
    [order?.requisites],
  );

  const formattedCardInfo = useMemo(() => {
    if (
      tradeMethodSelection.selectedTradeMethod?.paymentType ===
      PaymentType.Card2Card
    ) {
      return formatUtils.formatCardNumber(requisitesCardInfo);
    }
    return requisitesCardInfo;
  }, [
    requisitesCardInfo,
    tradeMethodSelection.selectedTradeMethod?.paymentType,
  ]);

  const requisites = useMemo(() => order?.requisites, [order?.requisites]);

  const isAccountNumberWithQr = useMemo(
    () =>
      !!(
        requisites?.qrImageUrl &&
        tradeMethodSelection.selectedTradeMethod?.paymentType ===
          PaymentType.AccountNumber
      ),
    [
      requisites?.qrImageUrl,
      tradeMethodSelection.selectedTradeMethod?.paymentType,
    ],
  );

  const requisitesPhone = useMemo(
    () => (requisites as RequisitesSBP)?.phone || '',
    [requisites],
  );

  const requisitesPaymentLink = useMemo(
    () => requisites?.paymentLink || '',
    [requisites?.paymentLink],
  );

  const providerPaymentUrl = useMemo(
    () => order?.providerPaymentUrl || '',
    [order?.providerPaymentUrl],
  );

  const getFirstRequisitesTradeMethodField = useCallback(() => {
    const field = first(tradeMethodSelection.selectedTradeMethod?.fields);
    const fieldValue = field && requisites?.[field.name as keyof Requisites];
    return isString(fieldValue) ? fieldValue : '';
  }, [requisites, tradeMethodSelection.selectedTradeMethod?.fields]);

  const paymentType = useMemo(
    () => order?.payment?.type,
    [order?.payment?.type],
  );

  const { deeplinks, desktopQrDeeplinks } = useDeeplinks({
    order,
    selectedTradeMethod: tradeMethodSelection.selectedTradeMethod,
    isDeeplink: paymentTypeFlags.isDeeplink,
    customerBankCode: bankUtils.customerBankCode,
  });

  const shouldShowQrCode = useMemo(() => {
    if (
      sciDomainConfig?.template === SciTemplate.SQ &&
      tradeMethodSelection.selectedTradeMethod &&
      SQ_PAYMENT_TYPES_SKIP_QR.includes(
        tradeMethodSelection.selectedTradeMethod.paymentType,
      )
    ) {
      return false;
    } else if (
      // the SQ view offers a QR handoff for OS-pinned links on desktop, so its
      // payment QR must also yield to them; other templates only react to
      // links openable on the current OS
      !isEmpty(
        sciDomainConfig?.template === SciTemplate.SQ
          ? desktopQrDeeplinks
          : deeplinks,
      )
    ) {
      return false;
    } else if (paymentTypeFlags.isMobileOperator) {
      return false;
    } else if (isAccountNumberWithQr) {
      return true;
    } else if (requisites?.qrImageUrl) {
      return true;
    } else if (paymentTypeFlags.isNSPK) {
      return true;
    } else if (
      tradeMethodSelection.selectedTradeMethod &&
      PAYMENT_TYPES_WITH_PAYMENT_LINK_QR.includes(
        tradeMethodSelection.selectedTradeMethod.paymentType,
      ) &&
      requisites?.paymentLink
    ) {
      return true;
    } else {
      return false;
    }
  }, [
    sciDomainConfig?.template,
    tradeMethodSelection.selectedTradeMethod,
    deeplinks,
    desktopQrDeeplinks,
    paymentTypeFlags.isMobileOperator,
    paymentTypeFlags.isNSPK,
    isAccountNumberWithQr,
    requisites?.qrImageUrl,
    requisites?.paymentLink,
  ]);

  const paymentFieldDefinitions = useMemo(() => {
    const selectedTradeMethod = tradeMethodSelection.selectedTradeMethod;
    const selectedTradeMethodFields = selectedTradeMethod?.fields ?? [];
    const fallbackPaymentType = selectedTradeMethod?.paymentType ?? paymentType;

    if (!isEmpty(selectedTradeMethodFields)) {
      return selectedTradeMethodFields;
    }

    return fallbackPaymentType
      ? PAYMENT_TYPE_REQUISITES_FIELD_FALLBACKS[fallbackPaymentType] ?? []
      : [];
  }, [paymentType, tradeMethodSelection.selectedTradeMethod]);

  const shouldShowBankFirst = useMemo(
    () =>
      isPhoneNumberKgs(
        tradeMethodSelection.selectedTradeMethod?.paymentType ?? paymentType,
        tradeMethodSelection.selectedTradeMethod?.fiatCurrency,
      ),
    [
      tradeMethodSelection.selectedTradeMethod?.paymentType,
      tradeMethodSelection.selectedTradeMethod?.fiatCurrency,
      paymentType,
    ],
  );

  const formattedPaymentFields: PaymentField[] = useMemo(() => {
    const bankField =
      (!paymentTypeFlags.isNSPK ||
        sciDomainConfig?.template === SciTemplate.SberQr) &&
      requisitesBank
        ? [{ name: 'bank', type: 'bank' }]
        : [];

    return [
      ...(shouldShowBankFirst ? bankField : []),
      ...paymentFieldDefinitions,
      ...(shouldShowBankFirst ? [] : bankField),
      ...(requisitesCountryCode
        ? [{ name: 'countryCode', type: 'country' }]
        : []),
    ]
      .filter((field) => {
        if (field.name === 'paymentString') {
          return false;
        }
        if (paymentTypeFlags.isDeeplink && field.name === 'paymentLink') {
          return false;
        }
        if (paymentTypeFlags.isTSim && field.name === 'paymentLink') {
          return false;
        }
        if (
          paymentTypeFlags.isNSPK &&
          field.name === 'paymentLink' &&
          shouldShowQrCode
        ) {
          return false;
        }
        if (paymentTypeFlags.isMobileOperator && field.name === 'cardholder') {
          return false;
        }
        const fieldValue = requisites?.[field.name as keyof Requisites];
        return isString(fieldValue) && (fieldValue as string).trim();
      })
      .filter((field) => {
        if (
          sciDomainConfig?.template === SciTemplate.SQ ||
          sciDomainConfig?.template === SciTemplate.SberQr
        ) {
          return field.name !== 'paymentLink';
        }
        return true;
      })
      .filter(() => {
        if (
          sciDomainConfig?.template === SciTemplate.SQ ||
          sciDomainConfig?.template === SciTemplate.SberQr
        ) {
          return !paymentTypeFlags.isNSPK;
        }
        return true;
      })
      .map(({ name, type }) => {
        let rawValue: string =
          (requisites?.[
            name as keyof Omit<Requisites, 'sameBank'>
          ] as string) || '';
        let label: string = tPaymentDetails(`${type}.label`, {
          defaultValue: type,
        });

        if (
          (tradeMethodSelection.selectedTradeMethod?.paymentType ??
            paymentType) === PaymentType.Card2Card &&
          name === 'cardInfo'
        ) {
          rawValue = formattedCardInfo;
        }

        if (requisitesBank && name === 'bank') {
          rawValue = bankUtils.requisitesBankName || '';
          if (paymentTypeFlags.isMobileOperator) {
            label = tPaymentDetails(`${type}.mobile_operator_label`, {
              defaultValue: type,
            });
          }
        }

        if (requisitesCountryCode && name === 'countryCode') {
          rawValue = requisitesCountry;
        }

        if (isAccountNumberWithQr && name === 'accountNumber') {
          rawValue = '';
          label = '';
        }

        return { name, type, label, value: rawValue };
      })
      .filter((f) => f.value || f.label);
  }, [
    paymentFieldDefinitions,
    shouldShowBankFirst,
    tradeMethodSelection.selectedTradeMethod?.paymentType,
    paymentType,
    paymentTypeFlags.isNSPK,
    paymentTypeFlags.isDeeplink,
    paymentTypeFlags.isTSim,
    paymentTypeFlags.isMobileOperator,
    requisitesBank,
    requisitesCountryCode,
    shouldShowQrCode,
    requisites,
    sciDomainConfig?.template,
    tPaymentDetails,
    isAccountNumberWithQr,
    formattedCardInfo,
    bankUtils.requisitesBankName,
    requisitesCountry,
  ]);

  const paymentStatusView: PaymentStatusView = useMemo(() => {
    if (
      order?.status === OrderStatus.New &&
      tradeMethodSelection.startPaymentSyncFailed
    ) {
      return PaymentStatusView.NoRequisites;
    }
    if (order?.status === OrderStatus.Dispute) {
      return !isEmpty(receipts.receipts)
        ? PaymentStatusView.Processing
        : PaymentStatusView.Dispute;
    }
    if (order?.status === OrderStatus.Cancelled) {
      if (
        order.statusDetails === CancelStatusDetails.CancelledRequisitesTimeout
      ) {
        return PaymentStatusView.NoRequisites;
      }
      if (
        order.statusDetails ===
        CancelStatusDetails.CancelledSyncRequisitesAttempts
      ) {
        return PaymentStatusView.AssignAttempts;
      }
      if (
        order.statusDetails ===
        CancelStatusDetails.CancelledCustomerConfirmTimeout
      ) {
        return PaymentStatusView.CustomerConfirmTimeout;
      }
      if (
        order.statusDetails ===
        CancelStatusDetails.CancelledCustomerAlternativesExhausted
      ) {
        return PaymentStatusView.NoRequisites;
      }
      return PaymentStatusView.Cancel;
    }
    return PaymentStatusView.Success;
  }, [
    order?.status,
    order?.statusDetails,
    receipts.receipts,
    tradeMethodSelection.startPaymentSyncFailed,
  ]);

  const isLoading = useMemo(
    () =>
      (!authenticated && isAuthLoading) ||
      !order ||
      isOrderLoading ||
      staticData.isBanksLoading ||
      staticData.isCurrenciesLoading ||
      tradeMethodSelection.isTradeMethodsLoading ||
      tradeMethodSelection.isShopSciTradeMethodsLoading ||
      receipts.isReceiptsLoading,
    [
      authenticated,
      isAuthLoading,
      order,
      isOrderLoading,
      staticData.isBanksLoading,
      staticData.isCurrenciesLoading,
      tradeMethodSelection.isTradeMethodsLoading,
      tradeMethodSelection.isShopSciTradeMethodsLoading,
      receipts.isReceiptsLoading,
    ],
  );

  const isInitialDataLoaded = useMemo(
    () =>
      staticData.isBanksSuccess &&
      staticData.isCurrenciesSuccess &&
      (!tradeMethodSelection.shouldLoadTradeMethods ||
        (order?.shop?.sciCustomTradeMethodsEnabled
          ? tradeMethodSelection.isShopSciTradeMethodsSuccess
          : tradeMethodSelection.isTradeMethodsSuccess)),
    [
      staticData.isBanksSuccess,
      staticData.isCurrenciesSuccess,
      tradeMethodSelection.shouldLoadTradeMethods,
      order?.shop?.sciCustomTradeMethodsEnabled,
      tradeMethodSelection.isShopSciTradeMethodsSuccess,
      tradeMethodSelection.isTradeMethodsSuccess,
    ],
  );

  const phoneEventFiredRef = useRef(false);
  const nameEventFiredRef = useRef(false);

  const gatewayLoadedRef = useRef(false);

  useEffect(() => {
    if (isInitialDataLoaded && !gatewayLoadedRef.current) {
      gatewayLoadedRef.current = true;
      eventUtils.trackEvent({ event: EventName.GatewayLoaded });
    }
  }, [isInitialDataLoaded]);

  useEffect(() => {
    if (isInitialDataLoaded && order?.status === OrderStatus.New) {
      if (!isEmpty(tradeMethodSelection.formattedTradeMethods)) {
        eventUtils.trackEvent({ event: EventName.OpenPaymentMethodSelection });
      } else if (tradeMethodSelection.shouldLoadTradeMethods) {
        eventUtils.trackEvent({ event: EventName.NoPaymentMethodsShown });
      }
    }
  }, [
    isInitialDataLoaded,
    order?.status,
    tradeMethodSelection.formattedTradeMethods,
    tradeMethodSelection.shouldLoadTradeMethods,
  ]);

  useEffect(() => {
    if (order?.status === OrderStatus.CustomerConfirm) {
      eventUtils.trackEvent({ event: EventName.RequisitesFound });
    }
  }, [order?.status]);

  useEffect(() => {
    if (paymentStatusView === PaymentStatusView.NoRequisites) {
      eventUtils.trackEvent({ event: EventName.RequisitesNotFound });
    }
  }, [paymentStatusView]);

  if (authenticationFailed) {
    return { isLoading: false, authenticationFailed: true };
  }

  if (isLoading || !order) {
    return { isLoading: true, authenticationFailed: false };
  }

  return {
    isLoading: false,
    authenticationFailed: false,
    orderId,
    order,
    status: order.status as OrderStatus,
    refetchOrder,
    refetchOrderStatus,
    ...staticData,
    tradeMethodSelection,
    paymentTypeFlags,
    paymentForm,
    ...bankUtils,
    receiptsResult: receipts,
    orderActions,
    paymentFormValues,
    currencySymbol,
    requisites,
    requisitesCountry,
    requisitesCountryCode,
    requisitesBank,
    bankStyle,
    getBankIconUrl,
    requisitesCardInfo,
    formattedCardInfo,
    isAccountNumberWithQr,
    shouldShowQrCode,
    requisitesPhone,
    requisitesPaymentLink,
    providerPaymentUrl,
    getFirstRequisitesTradeMethodField,
    paymentFieldDefinitions,
    formattedPaymentFields,
    paymentType,
    paymentStatusView,
    deeplinks,
    desktopQrDeeplinks,
    isTSBPSingleBankSelection,
    isStartPaymentSyncInProgress,
    isStartRequisitesCascadeLoading:
      orderActions.isStartRequisitesCascadeLoading,
    onPaymentFormValuesChange: handlePaymentFormValuesChange,
    onConfirmPayment: () => orderActions.confirmPayment(orderId),
    ...selectingAlternativeSum,
  };
};
